/**
 * Navbar Component
 * Design: "Signal & Noise" – Dark Tech
 * Sticky navbar, transparent on top → solid on scroll
 * Language switcher: DE 🇩🇪 / EN 🇬🇧
 * Performance: Framer Motion entfernt → reines CSS für kleineres Bundle
 */
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  const homeUrl = language === "en" ? "/?lang=en" : "/";
  const aiUrl = language === "en" ? "/llm-ki-optimierung?lang=en" : "/llm-ki-optimierung";
  const toHomeSection = (hash: string) => window.location.pathname === "/" ? hash : `${homeUrl}${hash}`;

  const navLinks = [
    { label: t.nav.services, href: toHomeSection("#services") },
    { label: t.nav.career, href: toHomeSection("#education") },
    { label: t.nav.references, href: toHomeSection("#references") },
    { label: t.nav.ai, href: aiUrl },
    { label: t.nav.faq, href: toHomeSection("#faq") },
    { label: t.nav.contact, href: toHomeSection("#contact") },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (!href.startsWith("#")) {
      window.location.href = href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const toggleLanguage = () => {
    setLanguage(language === "de" ? "en" : "de");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#080C14] border-b border-white/5 shadow-2xl"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a
            href={homeUrl}
            onClick={(e) => {
              if (window.location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            aria-label="CY – Cem Fernández-Yigit – Zur Startseite"
            className="flex items-center gap-2 group"
          >
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center font-sora font-bold text-white text-sm shadow-lg shadow-blue-500/30 group-hover:shadow-blue-500/50 transition-shadow">
              CY
            </div>
            <div className="hidden sm:block">
              <span className="font-sora font-bold text-white text-lg leading-none whitespace-nowrap">M.Sc. Cem Fernández-Yigit</span>
              <div className="text-[10px] text-blue-400 font-mono-custom tracking-widest uppercase">Performance Marketing</div>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="px-2.5 xl:px-3 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-white/5 transition-all duration-200 font-sora whitespace-nowrap"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right side: Language switcher + CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              title={language === "de" ? "Switch to English" : "Zu Deutsch wechseln"}
              aria-label={language === "de" ? "Switch to English" : "Zu Deutsch wechseln"}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/25 hover:bg-white/5 transition-all duration-200 group"
            >
              {language === "de" ? (
                <>
                  <span className="text-lg leading-none">🇬🇧</span>
                  <span className="text-xs font-mono-custom text-slate-400 group-hover:text-white transition-colors">EN</span>
                </>
              ) : (
                <>
                  <span className="text-lg leading-none">🇩🇪</span>
                  <span className="text-xs font-mono-custom text-slate-400 group-hover:text-white transition-colors">DE</span>
                </>
              )}
            </button>

            {/* CTA Button */}
            <a
              href={t.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary px-4 py-2.5 rounded-lg text-sm font-sora font-semibold whitespace-nowrap"
            >
              {t.nav.cta}
            </a>
          </div>

          {/* Mobile: Language + Menu Toggle */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              title={language === "de" ? "Switch to English" : "Zu Deutsch wechseln"}
              aria-label={language === "de" ? "Switch to English" : "Zu Deutsch wechseln"}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-white/10 hover:border-white/25 hover:bg-white/5 transition-all duration-200"
            >
              {language === "de" ? (
                <span className="text-base leading-none">🇬🇧</span>
              ) : (
                <span className="text-base leading-none">🇩🇪</span>
              )}
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
              aria-expanded={mobileOpen}
              className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu – reines CSS, kein Framer Motion */}
      <div
        className={`lg:hidden bg-[#0D1526] border-b border-white/5 overflow-hidden transition-all duration-300 ease-in-out ${
          mobileOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
        aria-hidden={!mobileOpen}
      >
        <div className="px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              className="w-full text-left px-4 py-3 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-white/5 transition-all duration-200 font-sora"
            >
              {link.label}
            </button>
          ))}
          <div className="pt-2 pb-1">
            <a
              href={t.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center btn-primary px-5 py-3 rounded-lg text-sm font-sora font-semibold"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
